import {
  Component,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core'
import { Subject, takeUntil } from 'rxjs'
import { MatSort } from '@angular/material/sort'
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator'
import { MatTableDataSource, MatTableModule } from '@angular/material/table'
import { SelectionModel } from '@angular/cdk/collections'
import { VersionComponent } from '@nx-superprep/shared'
import { DateOnly, countTitle, log } from '@nx-superprep/utils'
import { StudyPhase, DeckService, FrontendCard, StudyService, StudyOrderProgress } from '@nx-superprep/backend/firebase'
import { NgxJsonViewerModule } from 'ngx-json-viewer'
import { DatePipe, DecimalPipe, NgClass, NgStyle, PercentPipe } from '@angular/common'
import { MatCardModule } from '@angular/material/card'
import { MatExpansionModule } from '@angular/material/expansion'

@Component({
  standalone: true,
  selector: 'study-info',
  templateUrl: './info.component.html',
  styleUrls: ['./info.component.scss'],
  imports: [
    NgClass, NgStyle,
    DecimalPipe, DatePipe, PercentPipe,
    MatCardModule,
    MatExpansionModule,
    MatTableModule,
    MatPaginatorModule,
    VersionComponent,
    NgxJsonViewerModule
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class InfoComponent implements OnInit, OnDestroy {
  private _isClosed = false
  private onDestroy = new Subject<void>()

  @ViewChild('allCards', { read: MatSort, static: true }) allCardsSort!: MatSort
  @ViewChild(MatPaginator, { static: true }) matPaginator!: MatPaginator
  @ViewChild('deckCards', { read: MatSort, static: true }) deckCardsSort!: MatSort

  @Input() reposition: { card?: FrontendCard; responseTime?: number, index: number } = { index: -1 }

  download = { src: '/assets/img/icons/download.webp', alt: 'download' }
  parameter!: number

  displayedColumns0 = ['name', 'score', 'maturity', 'responseTime', 'review', 'gameplay']
  private _displayedColumns = ['#', 'name', 'score', 'maturity', 'responseTime', 'review', 'assets']

  studyOrderProgress: StudyOrderProgress | undefined
  detail: FrontendCard | undefined
  isLoading = false

  selection = new SelectionModel<FrontendCard>(false, undefined)
  public deckCardSource = new MatTableDataSource<FrontendCard>()
  public allCardSource = new MatTableDataSource<FrontendCard>()

  constructor(
    private deckService: DeckService,
    private studyService: StudyService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.deckService.selectedCard.pipe(takeUntil(this.onDestroy)).subscribe(() => {
      this.deckCardSource.data = this.deck?.currentCards() ?? []
      this.allCardSource.data = this.deck?.cards ?? []
      this.cdr.markForCheck()
    })

    if (this.lesson?.moduleId) {
      this.studyService.getStudyOrderProgress(this.lesson.moduleId)
        .pipe(takeUntil(this.onDestroy))
        .subscribe((studyOrderProgress) => {
          this.studyOrderProgress = studyOrderProgress
          this.cdr.markForCheck()
        })
    }
  }

  ngOnDestroy() {
    this.onDestroy.next()
    this.onDestroy.complete()
  }

  get deck() {
    return this.deckService.deck
  }

  get finishedStudyOrdersCount() {
    return this.studyOrderProgress?.finishedStudyOrders.flatMap(c => c.cardIds).length || 0
  }

  get remainingStudyOrdersCount() {
    return this.studyOrderProgress?.remainingStudyOrders.flatMap(c => c.cardIds).length || 0
  }

  get displayedColumns() {
    return this.deck?.isInReview ? this._displayedColumns : this._displayedColumns.filter(c => c !== 'review')
  }

  get lessons() {
    return [] as unknown as { name: string, displayName: string }[]
  }

  get lesson() {
    return this.deck?.dayPlanModule
  }

  get status() {
    return this.deck?.dayPlanModule
  }

  get acquiredIdsToday() {
    return this.deck?.acquiredIdsToday ?? []
  }

  get currentCard() {
    return this.deck?.getCurrentCard()
  }

  get studyProgress() {
    return this.deck?.studyProgress
  }

  get sessionTime() {
    return this.status?.actualStudyTime ?? 0
  }

  get maturityProgress() {
    return this.lesson?.progress
  }

  get phaseColor() {
    switch (this.deck?.phase) {
      case StudyPhase.Review: return 'red'
      case StudyPhase.Acquisition: return 'blue'
      default: return 'green'
    }
  }

  get gameplayStatus() {
    return this.currentCard?.gameplayStatus === 'react' ? 'React' : ''
  }

  get currentStatus() {
    return this.shortName(this.currentCard)
  }

  get lastStatus() {
    const card = this.reposition?.card
    if (!card || this._isClosed) return ''
    const acquisition = this.deck?.phase === StudyPhase.Acquisition
    const reviewDue = this.reviewDue(card)
    const state = (acquisition
      ? card.inAcquisition
        ? ` repositioned at ${this.reposition.index}`
        : reviewDue > 0
          ? ` review in ${reviewDue.toString()} day${reviewDue > 1 ? 's' : ''}`
          : ''
      : ' reviewed')
    return `${this.shortName(card)}${state}`
  }

  score(card: FrontendCard) {
    return card.score
  }

  response(card: FrontendCard) {
    return card.answerTimes
  }

  maturity(card: FrontendCard) {
    return card.maturityPoints
  }

  isAssetized(card: FrontendCard) {
    return card.template.isAssetized
  }

  gameplayType(card: FrontendCard) {
    return card.template.gameplay?.type ?? ''
  }

  shortName(card?: FrontendCard) {
    return card ? card.id.split('-').slice(1, 4).join('-') : ''
  }

  reviewDue(card: FrontendCard) {
    const diff = card.reviewDueDate ? DateOnly.diff(new Date(), card.reviewDueDate, 'days') : 0
    return Math.abs(diff) > 300 ? 0 : diff
  }

  isActive(row: FrontendCard) {
    const index = this.findIndex(row)
    const activeIndex = this.findIndex(this.currentCard)
    return index === activeIndex
  }

  isRepositioned(row: FrontendCard) {
    const index = this.findIndex(row)
    return index > 0 && index === this.reposition?.index
  }

  findIndex(card: FrontendCard | undefined) {
    return card ? this.findNameIndex(card.id) : -1
  }

  findNameIndex(id: string, cards = this.deck?.currentCards() ?? []) {
    if (!(id && cards.length > 0)) return -1
    return cards.findIndex((c: FrontendCard) => c.id === id) ?? -1
  }

  show(card: FrontendCard | undefined) {
    log.debug(card)
  }

  onStudyLog() {
    return this.deckService.getStudyLog()
  }

  countTitle(item: unknown[], singular: string, plural: string) {
    return countTitle(item, singular, plural)
  }
}
